import type { TipperCreatePayload } from "@/customTypings/tipper/Tipper";
import { authHeader, handleResponse } from "..";

export const TIPPER_CREATE_CONFLICT = {
    duplicateName: "tipper.duplicateName",
    duplicateLicence: "tipper.duplicateLicence",
    deletedRestoreAvailable: "tipper.deletedRestoreAvailable",
    duplicateAmbiguous: "tipper.duplicateAmbiguous",
} as const;

export type TipperDeletedRestoreAvailableConflict = {
    code: string;
    message: string;
    existingId: string;
    existingName: string;
};

export function isTipperDeletedRestoreAvailableConflict(
    value: unknown,
    expectedCode: string
): value is TipperDeletedRestoreAvailableConflict {
    return (
        typeof value === "object" &&
        value !== null &&
        (value as TipperDeletedRestoreAvailableConflict).code === expectedCode &&
        typeof (value as TipperDeletedRestoreAvailableConflict).existingId === "string"
    );
}

export class TipperCreateConflictError extends Error {
    readonly conflict: TipperDeletedRestoreAvailableConflict;

    constructor(conflict: TipperDeletedRestoreAvailableConflict) {
        super(conflict.message);
        this.name = "TipperCreateConflictError";
        this.conflict = conflict;
    }
}

export type TipperRestoreDeletedPayload = TipperCreatePayload & { id: string };

function siteTippersBase(siteId: string) {
    return `/api/sites/${encodeURIComponent(siteId)}/tippers`;
}

export async function postTipperCreate<TDetail>(
    siteId: string,
    payload: TipperCreatePayload
): Promise<TDetail> {
    const response = await fetch(`${siteTippersBase(siteId)}/create`, {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    });
    const text = await response.text();
    let data: unknown = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        /* non-JSON */
    }

    if (
        response.status === 409 &&
        isTipperDeletedRestoreAvailableConflict(data, TIPPER_CREATE_CONFLICT.deletedRestoreAvailable)
    ) {
        throw new TipperCreateConflictError(data);
    }

    if (!response.ok) {
        const err = data as { code?: string; message?: string } | null;
        if (err?.code === TIPPER_CREATE_CONFLICT.duplicateName) {
            throw new Error(err.message ?? "A tipper with this name already exists at this site.");
        }
        if (err?.code === TIPPER_CREATE_CONFLICT.duplicateLicence) {
            throw new Error(err.message ?? "A tipper with this carrier licence already exists at this site.");
        }
        if (err?.code === TIPPER_CREATE_CONFLICT.duplicateAmbiguous) {
            throw new Error(err.message ?? "Multiple deleted tippers match these details.");
        }
    }

    return handleResponse({
        text: async () => text,
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
    }) as Promise<TDetail>;
}
